// filepath: apps/web/src/definitions/useDefinition.ts
//
// TanStack Query wrapper that fetches a single definition and classifies
// the result into one of the 5 mandatory renderer states.
//
// IMPORTANT INVARIANTS:
//   - staleTime: Infinity (definitions are immutable by version, ARCH §6.2).
//   - gcTime: 30 min (per ARCHITECTURE_WEB.md §4).
//   - Retry is DISABLED — error classification handles each retry decision.
//   - Cache key includes tenantSlug (INV-1, see queryKeys.ts).
//   - No client-side formula evaluation. Visibility / computed fields are
//     passed through to the (future) FR-FRM-4 batch endpoint.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import {
  ApiError,
  type DefinitionKey,
  type GetFormDefinitionResponse,
  type RendererStatus,
} from "./types";
import { definitionQueryKey } from "./queryKeys";

const GC_TIME_MS = 30 * 60 * 1000;

/**
 * Map a definition `type` discriminator to its REST endpoint path.
 *
 * FR-UI-CORE-3: this is the SINGLE place where the type-to-URL mapping
 * is encoded. It is deliberately exhaustive (no default branch) so the
 * TypeScript compiler errors at build time when a new `type` is added
 * to `DefinitionKey` without a corresponding endpoint — preventing
 * the silent 404 / "permission-denied" path that would otherwise be
 * the only signal of a missed consumer.
 */
function definitionPath(type: DefinitionKey["type"]): string {
  switch (type) {
    case "form":
      return "form-definitions";
    case "process":
      // TODO(FR-UI-PROC-1): wire process-instance viewer when consumer lands.
      // The endpoint path is stable; the backend route will follow the
      // FR-FORM-2 pattern (`GET /api/process-definitions/{id}?version=N`).
      return "process-definitions";
    case "entity":
      // TODO(FR-UI-ENT-1): wire entity-definition editor when consumer lands.
      // The endpoint path is stable; the backend route will follow the
      // FR-FORM-2 pattern (`GET /api/entity-definitions/{id}?version=N`).
      return "entity-definitions";
  }
}

export interface UseDefinitionResult {
  data: GetFormDefinitionResponse | undefined;
  status: RendererStatus;
  isLoading: boolean;
  error: ApiError | null;
  refetch: () => Promise<unknown>;
}

/**
 * Fetches a definition keyed by (tenantSlug, type, id, version).
 *
 * @param key   Definition key. `undefined` skips the query (useful for
 *              conditional rendering).
 * @param apiBase Optional API base URL override. Defaults to `/api`.
 *               Mainly used in tests (MSW) and during dev with a proxy.
 */
export function useDefinition(
  key: DefinitionKey | undefined,
  apiBase = "/api",
): UseDefinitionResult {
  const { tenantSlug } = useTenant();

  const queryKey = key
    ? definitionQueryKey(tenantSlug, key)
    : ["definition", tenantSlug, "noop"];

  const query: UseQueryResult<GetFormDefinitionResponse, ApiError> = useQuery<
    GetFormDefinitionResponse,
    ApiError
  >({
    queryKey,
    queryFn: async () => {
      if (!key) {
        throw new ApiError("no key supplied", 0, "no_key");
      }
      // FR-UI-CORE-3: the URL path is dispatched on key.type so the same
      // hook can serve all three definition kinds (form / process / entity).
      //   - 'form'    → /form-definitions/{id}    (live today, FR-FORM-2)
      //   - 'process' → /process-definitions/{id} (planned for FR-UI-PROC-1)
      //   - 'entity'  → /entity-definitions/{id}  (planned for FR-UI-ENT-1)
      //
      // The endpoint for `process` / `entity` may not exist yet — in that
      // case the server returns 404 (unversioned) or 410 (versioned),
      // which `classifyResult` correctly routes to `permission-denied`
      // or `stale-version` respectively. No silent upgrade is possible
      // because the versioned 404 path never falls through to `'active'`.
      const path = definitionPath(key.type);
      const params = key.version === "active" ? "" : `?version=${key.version}`;
      const url = `${apiBase}/${path}/${encodeURIComponent(key.id)}${params}`;

      let response: Response;
      try {
        response = await apiFetch(url, {
          init: { headers: { Accept: "application/json" } },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const data = (await response.json()) as GetFormDefinitionResponse;
      return data;
    },
    enabled: key !== undefined,
    staleTime: Infinity,
    gcTime: GC_TIME_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    status: classifyResult(query, key),
    isLoading: query.isLoading,
    error: query.error,
    refetch: async () => {
      await query.refetch();
    },
  };
}

/**
 * Classifies a TanStack Query result into one of the 5 mandatory renderer
 * states. See 02-impact-analysis.md §4.4 for the disambiguation rules.
 *
 * - 401 / 403 → permission-denied (renderer cannot distinguish them and
 *   the user should be told "no access" in either case).
 * - 404 + versioned (not 'active') → stale-version (FR-BPM-10 invariant:
 *   the version they had is gone).
 * - 404 + 'active' → permission-denied (backend conflates 403 and 404
 *   for unversioned requests; the user expects access, otherwise they
 *   wouldn't ask).
 * - 410 Gone → stale-version (forward-compat — reserved for future
 *   "form definition permanently deleted" semantics).
 * - 429 → rate-limit (with retryAfterSeconds).
 * - 400 → fetch-failure (client supplied bad input).
 * - 5xx / network → fetch-failure.
 * - 200 → success.
 */
function classifyResult(
  query: {
    isLoading: boolean;
    isFetching: boolean;
    data: unknown;
    error: ApiError | null;
  },
  key: DefinitionKey | undefined,
): RendererStatus {
  if (query.isLoading || (query.isFetching && !query.data)) {
    return "loading";
  }
  if (query.error) {
    return classifyError(query.error, key);
  }
  if (query.data) {
    return "success";
  }
  return "loading";
}

function classifyError(
  err: ApiError,
  key: DefinitionKey | undefined,
): RendererStatus {
  if (err.status === 401 || err.status === 403) {
    return "permission-denied";
  }
  if (err.status === 429) {
    return "rate-limit";
  }
  if (err.status === 410) {
    return "stale-version";
  }
  if (err.status === 404) {
    // Server conflates 403 and 404 for unversioned requests (by INV-1).
    // A versioned 404 is unambiguously stale-version.
    if (key && key.version !== "active") {
      return "stale-version";
    }
    return "permission-denied";
  }
  // 400, 5xx, network (status 0)
  return "fetch-failure";
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function parseRetryAfter(raw: string | null): number | null {
  if (!raw) return null;
  const seconds = Number.parseInt(raw, 10);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  // HTTP-date variant — not handled in v1, surface null.
  return null;
}
